import { FocusableSectionProps, LeaveFor } from "@blacknut/spatialnav-sdk/dist";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FocusableSection } from "@blacknut/spatialnav-sdk/dist";
import {
  Entitlement,
  EntitlementCRMInfo,
  EntitlementKind,
  Image,
  isValidEntitlement,
  ImageKind,
  List,
  Page,
  getImageByOrientation,
} from "@blacknut/javascript-sdk/dist";
import { useInView } from "react-intersection-observer";
import { useOrientation } from "src/utils/OrientationContext";
import FadeInImage from "src/components/FadeInImage/FadeInImage";
import styles from "./styles.module.scss";
import { useDispatch, useSelector } from "react-redux";
import { AppLayout, getEntitlementCRMPriceInfo, passGetImages, State } from "core/redux";
import { logD, logE } from "@blacknut/logging/dist";
import { LOGGING_TAG } from "src/utils/Utils";
import { Button, ModalSubscription, useModal } from "@blacknut/react-sdk";
import { useLayout } from "core/utils";
import { ReactComponent as BuyIcon } from "../../../../assets/dist/ic_pass.svg";
import { ReactComponent as SvgPlaceholder } from "../../../../assets/dist/svgPlaceholder.svg";
import { QRCodeSVG } from "qrcode.react";
import { StorageKey, useAnalytics } from "core";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { isTryBuy } from "src/utils/subscription";
import { useSignupURL } from "src/utils/useSignupURL";
import { OrientationType } from "src/utils/OrientationContext";
import SwitchToMasterModal from "src/utils/SwitchToMasterModal";

const getBackground = (
  device: string,
  orientation: OrientationType,
  images?: Image[],
) => {
  let background = undefined;
  if (images) {
    if (
      (device === AppLayout.PHONE || device === AppLayout.TABLET) &&
      orientation === "LANDSCAPE"
    ) {
      background = getImageByOrientation(
        orientation,
        images.filter((img) => img.kind === ImageKind.HERO_LANDSCAPE),
      );
    } else {
      background = getImageByOrientation(
        orientation,
        images.filter((img) => img.kind === ImageKind.HERO),
      );
    }
  }
  return background;
};

interface HeroOfferViewProps
  extends Pick<FocusableSectionProps, "leaveFor" | "disabled"> {
  list: List;
  leaveFor?: LeaveFor;
  className?: string;
  page?: Page;
}

const HeroOfferView = ({ list, className }: HeroOfferViewProps) => {
  const [ref, inView] = useInView({
    threshold: 0,
    rootMargin: "33% 0px",
  });
  const { orientation } = useOrientation();
  const layout = useLayout();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { enrichSubURL } = useSignupURL();
  const { passImages } = useSelector((state: State) => state.passState);
  const { crmInfo } = useSelector((state: State) => state.entitlementState);
  const { subscription } = useSelector((state: State) => state.globalState);
  const { trackPass, trackSubscribeStripe } = useAnalytics();
  const [qrCodeShown, setQrCodeShown] = useState<boolean>(false);
  const [QrCodePurchaseURL, setQrCodePurchaseURL] = useState("");
  const [subURL, setSubURL] = useState("");
  const { profile } = useSelector((state: State) => state.profilesState);
  const pathname = location.pathname;
  const modalSubscription = useRef<ModalSubscription>();
  const { push: modalPush } = useModal();

  const closeModal = useCallback(() => {
    modalSubscription.current?.remove();
    modalSubscription.current = undefined;
  }, []);

  const isTVLegacy = localStorage.getItem(StorageKey.TV_LEGACY) === "true";

  const { entitlements } = subscription;
  let entitlement: Entitlement | undefined;
  if (list.entitlement) {
    entitlement = entitlements.find((e) => e.uuid == list.entitlement);
  }
  let myPassImages: Image[] | undefined;
  if (entitlement && entitlement.items?.length) {
    myPassImages = passImages[entitlement.items[0]];
  }
  let entitlementCRMInfo: EntitlementCRMInfo | undefined;
  if (list.entitlement) {
    entitlementCRMInfo = crmInfo[list.entitlement];
  }

  useEffect(() => {
    const init = async () => {
      if (
        !myPassImages &&
        entitlement?.kind !== EntitlementKind.PICK_YOUR_GAME &&
        entitlement?.items?.length
      ) {
        try {
          await passGetImages(entitlement.items[0])(dispatch);
        } catch (e) {
          logE(LOGGING_TAG, "Caught error getting pass images: %o", e);
        }
      }
      if (!entitlementCRMInfo && list.entitlement) {
        const main = entitlements.find((e) => e.kind === EntitlementKind.MAIN);
        try {
          await getEntitlementCRMPriceInfo({
            entitlementId: list.entitlement,
            bundle: !isValidEntitlement(main) || isTryBuy(main),
          })(dispatch);
        } catch (e) {
          logE(LOGGING_TAG, "Caught error getting entilement crm info: %o", e);
        }
      }
    };
    init();
  }, [
    dispatch,
    entitlement,
    entitlementCRMInfo,
    entitlements,
    list.entitlement,
    myPassImages,
  ]);

  useEffect(() => {
    const init = async () => {
      if (entitlementCRMInfo) {
        const res = await enrichSubURL(new URL(entitlementCRMInfo.subscribeLink));
        setSubURL(res.toString());
      }
    };
    init();
  }, [enrichSubURL, entitlementCRMInfo]);

  const onPurchase = useCallback(
    (url: string) => {
      if (!profile?.isMaster) {
        logD(LOGGING_TAG, "Profile is not master");

        const redirectUrl = pathname;
        modalSubscription.current = modalPush((props) => (
          <SwitchToMasterModal
            {...props}
            redirectUrl={redirectUrl}
            closeModal={closeModal}
          />
        ));
        return;
      }

      if (entitlement?.kind === EntitlementKind.MAIN) {
        trackSubscribeStripe();
      } else {
        const passId = entitlement?.items?.[0];
        passId && trackPass(passId);
      }

      if (layout === AppLayout.TV) {
        setQrCodePurchaseURL(url);
        setQrCodeShown(!qrCodeShown);
      } else {
        window.open(url);
      }
    },
    [
      closeModal,
      entitlement,
      layout,
      modalPush,
      pathname,
      profile?.isMaster,
      qrCodeShown,
      trackPass,
      trackSubscribeStripe,
    ],
  );

  const logo = myPassImages?.find((img) => img.kind === ImageKind.LOGO);
  let image = getBackground(layout, orientation, myPassImages);
  if (!image) {
    image = list?.imgBackground?.[0];
  }

  return (
    <div ref={ref} className={className}>
      <FocusableSection focusKey={list.uuid} className={styles.container}>
        {(inView || process.env.NODE_ENV === "test") && (
          <FadeInImage image={image} className={styles.image} />
        )}
        {entitlementCRMInfo?.subscribeLink && (
          <div
            data-testid="svgPlaceholder"
            className={clsx([
              styles.svgPlaceholder,
              qrCodeShown && styles.displayed,
              !isTVLegacy && styles.smoothTransition,
            ])}
          >
            <div>
              <SvgPlaceholder />
              <div
                dangerouslySetInnerHTML={{
                  __html: t("heroOffer.qrCodeText"),
                }}
              ></div>
              <QRCodeSVG
                title={QrCodePurchaseURL}
                className={styles.qrCode}
                value={QrCodePurchaseURL}
                marginSize={2}
              />
            </div>
          </div>
        )}

        <div className={styles.content}>
          {logo && <img src={logo.url} className={styles.logo} />}
          <h2 className={styles.title}>{entitlementCRMInfo?.heroText}</h2>
          {entitlementCRMInfo?.heroItems && (
            <ul className={styles.description}>
              {entitlementCRMInfo?.heroItems.map((item) => (
                <li key={item.text}>
                  {item.picto && <img className={styles.picto} src={item.picto} />}
                  <span dangerouslySetInnerHTML={{ __html: `${item.text}` }} />
                </li>
              ))}
            </ul>
          )}

          <div className={styles.buttonsContainer}>
            {entitlementCRMInfo?.subscribeLink && (
              <Button
                variant="primary"
                className={styles.buyButton}
                onClick={() => onPurchase(subURL)}
                onBlur={() => setQrCodeShown(false)}
                disabled={!subURL}
              >
                <BuyIcon /> {entitlementCRMInfo?.heroButton}
              </Button>
            )}

            {entitlementCRMInfo?.heroPartners.map((partner) => (
              <Button
                variant="secondary"
                key={partner.heading}
                className={styles.buyButton}
                onClick={() => onPurchase(partner.url)}
                onBlur={() => setQrCodeShown(false)}
              >
                <img src={partner.picto} className={styles.partnerPicto} />
                {partner.heading}
              </Button>
            ))}
          </div>

          {entitlementCRMInfo?.legal && (
            <span className={styles.disclaimer}>{entitlementCRMInfo?.legal}</span>
          )}
        </div>
      </FocusableSection>
    </div>
  );
};

export default HeroOfferView;
export { HeroOfferView };
